import { activateOffer, fetchStorefront, fetchStorefrontSubscription, retryOffer, subscribeStorefront, waitForSubscriptionStatus } from "./api";
import { emitRecurringEvent } from "./events";
import { bindManage, isCancelScheduled, isRetryable, manageBody, when } from "./manage";
import type { StorefrontCoin, StorefrontNetwork, StorefrontPlan, StorefrontPlanOption, SubscriptionOffer } from "./types";
import {
  amountHero,
  backButton,
  chipRow,
  errorBox,
  hint,
  infoBox,
  loadingRow,
  loadingShell,
  paymentShell,
  pickButton,
  primaryButton,
  stepDots,
  successBlock,
  summaryList,
  textField,
} from "./ui";
import { connectAndApprove, connectWallet, prefetchWallet } from "./wallet";

function slugFromPath(): string | null {
  const path = window.location.pathname.replace(/\/$/, "");
  const match = path.match(/^\/s\/([^/]+)$/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function intervalLabel(seconds: number): string {
  if (seconds >= 2592000 && seconds % 2592000 === 0) {
    const n = seconds / 2592000;
    return n === 1 ? "every month" : `every ${n} months`;
  }
  if (seconds >= 86400 && seconds % 86400 === 0) {
    const n = seconds / 86400;
    return n === 1 ? "every day" : `every ${n} days`;
  }
  if (seconds >= 3600 && seconds % 3600 === 0) {
    const n = seconds / 3600;
    return n === 1 ? "every hour" : `every ${n} hours`;
  }
  if (seconds >= 60 && seconds % 60 === 0) {
    return `every ${seconds / 60} min`;
  }
  return `every ${seconds}s`;
}

function cyclesLabel(cycles: number): string {
  return cycles > 0 ? `${cycles} charges` : "until cancelled";
}

function offerChips(offer: SubscriptionOffer): string {
  const items: Array<{ icon: string; label: string }> = [];
  if (offer.icon_coin_url && offer.token_symbol) {
    items.push({ icon: offer.icon_coin_url, label: offer.token_symbol });
  }
  const net = offer.short_name || offer.network_name;
  if (offer.icon_network_url && net) {
    items.push({ icon: offer.icon_network_url, label: net });
  }
  return chipRow(items);
}

function boundNetwork(coin: StorefrontCoin): StorefrontNetwork | null {
  return coin.networks?.[0] || null;
}

function isManagedStatus(status: string): boolean {
  return ["active", "past_due", "exhausted", "cancelled"].includes(status);
}

function parseHex(value: string): [number, number, number] | null {
  const match = /^#([0-9a-f]{6})$/i.exec(value.trim());
  if (!match) return null;
  const n = Number.parseInt(match[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mixHex(hex: string, other: string, amount: number): string {
  const a = parseHex(hex);
  const b = parseHex(other);
  if (!a || !b) return hex;
  const ch = (i: number) => Math.round(a[i] + (b[i] - a[i]) * amount);
  return `#${[ch(0), ch(1), ch(2)].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

function applyBrandColor(color?: string | null): void {
  const hex = color && parseHex(color) ? color.trim() : "";
  if (!hex) return;
  const root = document.documentElement.style;
  root.setProperty("--primary-300", mixHex(hex, "#ffffff", 0.45));
  root.setProperty("--primary-400", mixHex(hex, "#ffffff", 0.22));
  root.setProperty("--primary-500", hex);
  root.setProperty("--primary-600", mixHex(hex, "#000000", 0.18));
  root.setProperty("--primary-700", mixHex(hex, "#000000", 0.32));
}

type CachedBrand = { title?: string; logo_url?: string | null; color?: string | null };

function brandCacheKey(slug: string): string {
  return `fg-recurring-brand:${slug}`;
}

function readBrandCache(slug: string): CachedBrand | null {
  try {
    const raw = sessionStorage.getItem(brandCacheKey(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedBrand;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeBrandCache(slug: string, plan: StorefrontPlan): void {
  try {
    sessionStorage.setItem(
      brandCacheKey(slug),
      JSON.stringify({
        title: plan.title,
        logo_url: plan.logo_url,
        color: plan.color,
      } satisfies CachedBrand)
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function preloadLogo(url?: string | null): Promise<void> {
  if (!url) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    const done = () => resolve();
    const timer = window.setTimeout(done, 1200);
    const finish = () => {
      window.clearTimeout(timer);
      done();
    };
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
    if (typeof img.decode === "function") {
      void img.decode().then(finish, finish);
    }
  });
}

function collectionPlans(plan: StorefrontPlan): StorefrontPlanOption[] {
  if (plan.plans?.length) return plan.plans;
  return [
    {
      plan_id: "default",
      title: plan.title,
      amount_fiat: plan.amount_fiat,
      interval_seconds: plan.interval_seconds,
      cycles_authorized: plan.cycles_authorized,
    },
  ];
}

export class StorefrontApp {
  private phase: "plans" | "summary" | "contact" | "currency" = "summary";
  private coin: StorefrontCoin | null = null;
  private network: StorefrontNetwork | null = null;
  private customerName = "";
  private customerEmail = "";
  private reference = new URLSearchParams(window.location.search).get("reference")?.trim() || "";
  private lockedPlanId = new URLSearchParams(window.location.search).get("plan_id")?.trim() || "";
  private busy = false;
  private fromExisting = false;
  private collection: StorefrontPlan | null = null;
  private selectedPlanId: string | null = null;

  constructor(private root: HTMLElement) {}

  private shell(opts: { title?: string; subtitle?: string; body: string }): string {
    const collection = this.collection;
    return paymentShell({
      title: opts.title || collection?.title || "Subscription",
      subtitle: opts.subtitle,
      body: opts.body,
      logoUrl: collection?.logo_url,
      brandName: collection?.title || "Flowgate",
    });
  }

  private chosenPlan(plan: StorefrontPlan): StorefrontPlanOption {
    const plans = collectionPlans(plan);
    return plans.find((item) => item.plan_id === this.selectedPlanId) || plans[0];
  }

  async init(): Promise<void> {
    const slug = slugFromPath();
    if (!slug) {
      this.root.innerHTML = `<div class="page"><div class="shell"><div class="pay-card"><div class="pay-body">${errorBox("Missing storefront")}</div></div></div></div>`;
      return;
    }
    const cached = readBrandCache(slug);
    if (cached?.color) applyBrandColor(cached.color);
    if (cached?.title) document.title = cached.title;
    if (cached?.logo_url) {
      this.root.innerHTML = paymentShell({
        title: cached.title || "Subscription",
        body: loadingRow("Loading plan…"),
        logoUrl: cached.logo_url,
        brandName: cached.title,
      });
    } else {
      this.root.innerHTML = loadingShell("Loading plan…");
    }
    prefetchWallet();
    try {
      const plan = await fetchStorefront(slug);
      this.collection = plan;
      applyBrandColor(plan.color);
      writeBrandCache(slug, plan);
      if (plan.title) document.title = plan.title;
      await preloadLogo(plan.logo_url);
      const plans = collectionPlans(plan);
      const locked = plans.find((item) => item.plan_id === this.lockedPlanId);
      if (locked) this.selectedPlanId = locked.plan_id;
      else if (plans.length === 1) this.selectedPlanId = plans[0].plan_id;
      if (this.reference) {
        const existing = await fetchStorefrontSubscription(plan.slug, this.reference);
        if (existing && existing.status !== "pending_approve") {
          await this.resumeExisting(existing);
          return;
        }
      }
      this.phase = this.selectedPlanId || plans.length === 1 ? "summary" : "plans";
      this.renderPlan(plan);
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to load plan";
      this.root.innerHTML = cached?.logo_url
        ? paymentShell({
            title: cached.title || "Subscription",
            body: errorBox(msg),
            logoUrl: cached.logo_url,
            brandName: cached.title,
          })
        : `<div class="page"><div class="shell"><div class="pay-card"><div class="pay-body">${errorBox(msg)}</div></div></div></div>`;
    }
  }

  private renderPlan(plan: StorefrontPlan, extra = ""): void {
    const coins = plan.assets || [];
    const chosen = this.chosenPlan(plan);
    const amount = `${chosen.amount_fiat} ${plan.currency_fiat}`;
    const caption = `${intervalLabel(chosen.interval_seconds)} · ${cyclesLabel(chosen.cycles_authorized)}`;
    const notReady = !plan.ready
      ? errorBox("This storefront is not ready yet. The merchant needs to open the POS in the dashboard once.")
      : "";
    const plans = collectionPlans(plan);

    if (this.phase === "plans") {
      this.root.innerHTML = this.shell({
        body: `
          ${plans
            .map((item) =>
              pickButton({
                key: "plan",
                value: item.plan_id,
                title: item.title,
                subtitle: `${item.amount_fiat} ${plan.currency_fiat} · ${intervalLabel(item.interval_seconds)}`,
              })
            )
            .join("")}
          ${notReady}
          ${extra}
        `,
      });
      this.root.querySelectorAll<HTMLButtonElement>("[data-plan]").forEach((btn) => {
        btn.addEventListener("click", () => {
          this.selectedPlanId = btn.dataset.plan || null;
          this.phase = "summary";
          this.renderPlan(plan);
        });
      });
      return;
    }

    if (this.phase === "summary") {
      this.root.innerHTML = this.shell({
        subtitle: chosen.title,
        body: `
          ${amountHero(amount, caption)}
          ${summaryList([
            { label: "Plan", value: chosen.title },
            { label: "Amount", value: amount },
            { label: "Frequency", value: intervalLabel(chosen.interval_seconds) },
            { label: "Duration", value: cyclesLabel(chosen.cycles_authorized) },
          ])}
          ${plan.ready && !this.busy ? primaryButton("subscribe", "Subscribe with crypto") : ""}
          ${plans.length > 1 && !this.lockedPlanId ? backButton("back-plans", "plans") : ""}
          ${notReady}
          ${extra}
        `,
      });
      this.root.querySelector("#subscribe")?.addEventListener("click", () => {
        if (!plan.ready) return;
        void this.onSubscribe(plan);
      });
      this.root.querySelector("#back-plans")?.addEventListener("click", () => {
        this.phase = "plans";
        this.renderPlan(plan);
      });
      return;
    }

    if (this.phase === "contact") {
      this.root.innerHTML = this.shell({
        subtitle: chosen.title,
        body: `
          ${amountHero(amount, caption)}
          ${stepDots(3, 1)}
          ${backButton("back-summary", "summary")}
          <form id="contact-form" class="fields">
            ${textField({
              id: "customer-name",
              label: "Your name",
              value: this.customerName,
              placeholder: "Enter your name",
              autocomplete: "name",
            })}
            ${textField({
              id: "customer-email",
              label: "Your e-mail",
              type: "email",
              value: this.customerEmail,
              placeholder: "Enter your e-mail",
              autocomplete: "email",
            })}
            ${primaryButton("contact-next", "Continue", "submit")}
          </form>
          ${extra}
        `,
      });
      const form = this.root.querySelector<HTMLFormElement>("#contact-form");
      const nameInput = this.root.querySelector<HTMLInputElement>("#customer-name");
      const emailInput = this.root.querySelector<HTMLInputElement>("#customer-email");
      const readContact = () => {
        this.customerName = nameInput?.value.trim() || "";
        this.customerEmail = emailInput?.value.trim() || "";
      };
      const goNext = (event?: Event) => {
        event?.preventDefault();
        readContact();
        if (this.customerName.length < 2) {
          this.renderPlan(plan, errorBox("Name is required"));
          return;
        }
        if (!this.customerEmail.includes("@") || !this.customerEmail.split("@").pop()?.includes(".")) {
          this.renderPlan(plan, errorBox("A valid email is required"));
          return;
        }
        this.phase = "currency";
        this.renderPlan(plan);
      };
      this.root.querySelector("#back-summary")?.addEventListener("click", () => {
        readContact();
        this.phase = "summary";
        this.renderPlan(plan);
      });
      form?.addEventListener("submit", goNext);
      this.root.querySelector("#contact-next")?.addEventListener("click", goNext);
      return;
    }

    const chips = this.coin
      ? chipRow([{ icon: this.coin.icon_url, label: this.coin.code }])
      : "";
    const picker = `<div class="picker">${coins
      .map((coin) =>
        pickButton({
          key: "coin",
          value: coin.code,
          icon: coin.icon_url,
          title: coin.name || coin.code,
          subtitle: coin.code !== coin.name ? coin.code : undefined,
        })
      )
      .join("")}</div>`;
    const empty = coins.length ? "" : errorBox("No crypto currencies are available right now.");

    this.root.innerHTML = this.shell({
      subtitle: chosen.title,
      body: `
        ${amountHero(amount, caption)}
        ${stepDots(3, this.coin ? 3 : 2)}
        ${chips}
        ${backButton("back-contact", "your details")}
        ${picker}
        ${empty}
        ${extra}
      `,
    });

    this.root.querySelector("#back-contact")?.addEventListener("click", () => {
      this.phase = "contact";
      this.coin = null;
      this.network = null;
      this.renderPlan(plan);
    });
    this.root.querySelectorAll<HTMLButtonElement>("[data-coin]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const code = btn.dataset.coin || "";
        this.coin = coins.find((c) => c.code === code) || null;
        this.network = this.coin ? boundNetwork(this.coin) : null;
        if (!this.network) {
          this.renderPlan(plan, errorBox("This currency is not configured."));
          return;
        }
        void this.start(plan);
      });
    });
  }

  private manageHeadline(offer: SubscriptionOffer): { title: string; subtitle: string } {
    switch (offer.status) {
      case "active":
        if (isCancelScheduled(offer)) {
          return {
            title: "Cancellation scheduled",
            subtitle: `You'll keep access until ${when(offer.cancel_at)}. No further charges.`,
          };
        }
        return this.fromExisting
          ? {
              title: "Your subscription is already active",
              subtitle: "You can review charges or cancel below.",
            }
          : {
              title: "Subscription is active",
              subtitle: "Save this page to view charges or cancel later.",
            };
      case "exhausted":
        return {
          title: "This subscription has finished",
          subtitle: "All authorized charges have been collected.",
        };
      case "cancelled":
        return {
          title: "This subscription is cancelled",
          subtitle: "Future charges are stopped.",
        };
      default:
        return {
          title: `Subscription is ${offer.status}`,
          subtitle: "Save this page to view charges or cancel later.",
        };
    }
  }

  private renderOffer(offer: SubscriptionOffer, extra = ""): void {
    const waiting = offer.status === "confirming";
    const managed = isManagedStatus(offer.status);
    const amount = `${offer.amount_fiat} ${offer.currency_fiat}`;
    const headline = this.manageHeadline(offer);
    const body = waiting
      ? `${amountHero(amount, "First payment")}
        ${offerChips(offer)}
        ${loadingRow("Waiting for blockchain confirmation…")}
        ${hint("This can take a minute. Don’t close this window.")}
        ${extra}`
      : isRetryable(offer)
      ? `${amountHero(amount, "Subscription amount")}
        ${offerChips(offer)}
        ${manageBody(offer, extra)}`
      : managed
      ? `${offer.status === "cancelled" ? "" : successBlock({
          amount,
          caption: "Subscription amount",
          title: headline.title,
          subtitle: headline.subtitle,
          chips: offerChips(offer),
        })}
        ${offer.status === "active" && this.fromExisting ? infoBox("You already have an active subscription for this account.") : ""}
        ${manageBody(offer, extra)}`
      : `
        ${amountHero(amount, "Approve a capped allowance")}
        ${offerChips(offer)}
        ${extra}
      `;
    this.root.innerHTML = this.shell({
      title: offer.title,
      subtitle: `Later charges pull ${offer.token_symbol} to a Flowgate invoice`,
      body,
    });
    if (managed) {
      bindManage(this.root, offer, (next) => {
        this.renderOffer(next);
        if (next.status === "confirming") {
          void this.waitUntilSettled(next);
        }
      });
    }
  }

  private async onSubscribe(plan: StorefrontPlan): Promise<void> {
    if (this.busy) return;
    if (!this.reference) {
      this.phase = "contact";
      this.renderPlan(plan);
      return;
    }
    this.busy = true;
    this.renderPlan(plan, loadingRow("Checking your subscription…"));
    try {
      const existing = await fetchStorefrontSubscription(plan.slug, this.reference);
      this.busy = false;
      if (!existing) {
        this.phase = "contact";
        this.renderPlan(plan);
        return;
      }
      await this.resumeExisting(existing);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Could not check subscription";
      this.renderPlan(plan, errorBox(msg));
    }
  }

  private async resumeExisting(offer: SubscriptionOffer): Promise<void> {
    this.fromExisting = offer.status !== "pending_approve";
    if (offer.status === "pending_approve") {
      await this.approve(offer);
      return;
    }
    if (offer.status === "confirming") {
      this.renderOffer(offer);
      await this.waitUntilSettled(offer);
      return;
    }
    if (offer.status === "past_due") {
      await this.retryInBackground(offer);
      return;
    }
    this.renderOffer(offer);
  }

  private async retryInBackground(offer: SubscriptionOffer): Promise<void> {
    this.renderOffer(offer, loadingRow("Trying the payment again…"));
    try {
      const next = await retryOffer(offer.subscription_id);
      if (next.status === "confirming") {
        this.renderOffer(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      this.renderOffer(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Could not retry payment";
      this.renderOffer(offer, errorBox(msg));
    }
  }

  private async waitUntilSettled(offer: SubscriptionOffer): Promise<void> {
    try {
      const paid = await waitForSubscriptionStatus(offer.subscription_id, (item) =>
        isManagedStatus(item.status)
      );
      this.busy = false;
      if (paid.status === "cancelled") {
        emitRecurringEvent("subscription.failed", paid);
        this.renderOffer(paid, errorBox(paid.last_error || "Payment was not confirmed"));
        return;
      }
      if (paid.status === "past_due") {
        emitRecurringEvent("subscription.failed", paid);
        this.renderOffer(paid);
        return;
      }
      emitRecurringEvent("subscription.active", paid);
      this.renderOffer(paid);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Confirmation timed out";
      this.renderOffer(offer, errorBox(msg));
    }
  }

  private async start(plan: StorefrontPlan): Promise<void> {
    if (!this.network || this.busy) return;
    this.busy = true;
    this.fromExisting = false;
    this.renderPlan(plan, loadingRow("Opening wallet…"));
    const connecting = connectWallet(this.network.chain_id);
    try {
      const offer = await subscribeStorefront(plan.slug, this.network.id, {
        name: this.customerName,
        email: this.customerEmail,
        reference: this.reference,
        planId: this.selectedPlanId,
      });
      if (offer.status !== "pending_approve") {
        this.busy = false;
        void connecting.catch(() => undefined);
        await this.resumeExisting(offer);
        return;
      }
      emitRecurringEvent("subscription.created", offer);
      await this.approve(offer);
    } catch (error) {
      this.busy = false;
      void connecting.catch(() => undefined);
      const msg = error instanceof Error ? error.message : "Subscribe failed";
      this.renderPlan(plan, errorBox(msg));
    }
  }

  private async approve(offer: SubscriptionOffer): Promise<void> {
    try {
      const net = offer.network_name || offer.short_name || "the required network";
      this.renderOffer(
        offer,
        loadingRow(`Connect a wallet and approve ${offer.token_symbol} on ${net}…`)
      );
      const { payer, tx } = await connectAndApprove({
        chainId: offer.chain_id,
        token: offer.token_contract,
        spender: offer.spender,
        amountRaw: BigInt(offer.approve_amount_raw),
      });
      const next = await activateOffer(offer.subscription_id, payer, tx);
      if (next.status === "confirming") {
        emitRecurringEvent("subscription.confirming", next);
        this.renderOffer(next);
        await this.waitUntilSettled(next);
        return;
      }
      this.busy = false;
      if (next.status === "active" || next.status === "exhausted") {
        emitRecurringEvent("subscription.active", next);
      }
      this.renderOffer(next);
    } catch (error) {
      this.busy = false;
      const msg = error instanceof Error ? error.message : "Approve failed";
      this.renderOffer(
        offer,
        errorBox(msg) +
          hint("MetaMask should ask to approve the token, not send 0 ETH. Switch to the shown network and retry.")
      );
    }
  }
}
